import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { FileUploadModule } from 'ng2-file-upload';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { ColumnDefinition } from 'src/app/controls/advanced-gridview/models/column-definition';
import { ColumnListDefinition } from 'src/app/controls/advanced-gridview/models/column-list-definition';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { Context } from 'src/app/controls/advanced-gridview/models/context';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { AdvancedService } from 'src/app/controls/advanced-gridview/services/advanced-service';
import { CrudFormComponent } from 'src/app/controls/crud-form/crud-form.component';
import { FilterType } from 'src/app/controls/filters/models/filter-type';
import { ListItem } from 'src/app/controls/filters/models/list-item';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { ArticleAlias } from 'src/app/models/article-alias';
import { Rights } from 'src/app/models/enums/rights';
import { IArticle } from 'src/app/models/interfaces/article.interface';
import { ArticleAliasesService } from 'src/app/services/article-aliases.service';
import { CustomersService } from 'src/app/services/customers.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { CustomerAliasDialogComponent } from './customer-alias-dialog/customer-alias-dialog.component';

import { AdvancedGridviewComponent } from '../../../controls/advanced-gridview/advanced-gridview.component';

@Component({
    selector: 'app-article-alias-form',
    templateUrl: '../../../controls/crud-form/crud-form.component.html',
    styleUrls: ['../../../controls/crud-form/crud-form.component.scss'],
    imports: [AdvancedGridviewComponent, MatButtonModule, FileUploadModule, NgxSpinnerModule]
})
export class ArticleAliasFormComponent extends CrudFormComponent<ArticleAlias, IArticle, CustomerAliasDialogComponent> {
    /**
     * Constructeur.
     * @param enumHelper: Classe utilitaire pour les énumérations.
     * @param notificationService: Service utilisé pour gérer l'affichage des notifications.
     * @param spinner: Service utilisé pour gérer l'affichage du spinner.
     * @param dialog: Service pour ouvrir des boites de dialogue.
     * @param memoryService: Service pour récupérer les informations en mémoire.
     */
    public constructor(
        private customersService: CustomersService,
        private articleAliasesService: ArticleAliasesService,
        public override enumHelper: EnumHelper,
        protected override notificationService: NotificationService,
        protected override spinner: NgxSpinnerService,
        public override dialog: MatDialog,
        public override memoryService: MemoryService
    ) {
        super(enumHelper, notificationService, spinner, dialog, memoryService);
    }

    /**
     * Définition des colonnes de la Gridview.
     */
    override get defaultTableConfig(): ColumnDefinition[] {
        return [
            new ColumnListDefinition<ArticleAlias>('customerId', 'Client', FilterType.List, 2, 10, true, false),
            new ColumnDefinition('alias', 'Alias', ColumnType.Text, FilterType.Text, 3, 85, true),
        ];
    }

    /**
     * Définition des colonnes de la Gridview.
     */
    override get withIdTableConfig(): ColumnDefinition[] {
        return [
            new ColumnDefinition('id', 'Id', ColumnType.Text, FilterType.Text, 1, 5, true),
            new ColumnListDefinition<ArticleAlias>('customerId', 'Client', FilterType.List, 2, 10, true, false),
            new ColumnDefinition('alias', 'Alias', ColumnType.Text, FilterType.Text, 3, 80, true),
        ];
    }

    /**
     * Charge les éléments des filtres d'une colonnes de type liste.
     * @param datasourceContext
     * @param columns
     */
    protected override loadListColumnDatas(datasourceContext: Context, columns: ColumnListDefinition<string>[]): void {
        this.customersService.List(datasourceContext, null, null, null).subscribe((x) => {
            var listItem = x.value.map<ListItem<string>>(
                (v) =>
                    <ListItem<string>>{
                        value: v.id.toString(),
                        text: v.name,
                    }
            );
            this.setColumnFilterDatas(
                columns.find((x) => x.columnName === 'customerId'),
                listItem
            );
        });
    }

    /**
     * Instancie un nouvel enregistrement.
     */
    protected override instantiateNewRecord(): ArticleAlias {
        let record = new ArticleAlias();
        record.articleId = this._context.item.id;
        return record;
    }

    /**
     * Retourne le texte de confirmation de la suppression.
     */
    protected override getDeleteConfirmationText(item: ArticleAlias): string {
        return `Vous êtes sur le point de supprimer l'alias '${item.id}'.`;
    }

    /**
     * Retourne le texte de suppression.
     */
    protected override getDeletionText(): string {
        return 'Alias supprimé';
    }

    /**
     * Retourne le texte d'erreur de suppression.
     */
    protected override getDeletionErrorText(): string {
        return "Impossible de supprimer l'alias.";
    }

    /**
     * Retourne le texte de création.
     */
    protected override getCreationText(): string {
        return 'Alias créé';
    }

    /**
     * Retourne le texte d'erreur de création.
     */
    protected override getCreationErrorText(): string {
        return "Impossible de créer l'alias.";
    }

    /**
     * Retourne le texte de mise à jour.
     */
    protected override getUpdateText(): string {
        return 'Alias mis à jour';
    }

    /**
     * Retourne le texte d'erreur de mise à jour.
     */
    protected override getUpdateErrorText(): string {
        return "Impossible de mettre à jour l'alias.";
    }

    /**
     * Retourne la requête pour supprimer un enregistrement.
     * @param item : Instance de l'enregistrement à supprimer.
     */
    protected override getDeletionRequest(item: ArticleAlias): Observable<boolean> {
        return this.articleAliasesService.delete(item.articleId, item.id);
    }

    /**
     * Retourne la requête pour créer un enregistrement.
     * @param item : Instance de l'enregistrement à créer.
     */
    protected override getCreationRequest(item: ArticleAlias): Observable<ArticleAlias> {
        return this.articleAliasesService.add(item.articleId, item);
    }

    /**
     * Retourne la requête pour mettre à jour un enregistrement.
     * @param item : Instance de l'enregistrement à mettre à jour.
     */
    protected override getUpdateRequest(item: ArticleAlias): Observable<ArticleAlias> {
        return this.articleAliasesService.update(item.articleId, item.id, item);
    }

    /**
     * Retourne la requête pour uploader le fichier d'import.
     */
    protected override getUploadRequest(): string {
        return `${this.baseUrl}/api/organizations/${this.organizationId}/articles/${this._context.item.id}/articlealiases/import`;
    }

    /**
     * Retourne le composant boite de dialogue utilisé pour afficher le formulaire de création / édition / consultation.
     */
    protected override getDialogComponent(): new (...args: any[]) => CustomerAliasDialogComponent {
        return CustomerAliasDialogComponent;
    }

    /**
     * Retourne le nom du fichier d'export.
     */
    protected override getExportFileName(): string {
        return `Alias de l\'article ${this._context.item.id}.xlsx`;
    }

    /**
     * Retourne le duplicata d'un enregistrement.
     * @param item : Instance de l'enregistrement à dupliquer.
     */
    protected override duplicate(item: ArticleAlias): ArticleAlias {
        let record = new ArticleAlias();
        record.articleId = item.articleId;
        record.customerId = item.customerId;
        record.alias = item.alias;
        return record;
    }

    /**
     * Retourne la source de données de la liste.
     */
    protected override getDatasourceService(): AdvancedService<ArticleAlias> {
        return this.articleAliasesService;
    }

    /**
     * Retourne le contexte utilisé par la source de données de la liste.
     */
    protected override getDatasourceContext(): Context {
        return { property: 'articleId', value: this._context.item.id };
    }

    /**
     * Retourne les infos de tri par défaut de la liste.
     */
    protected override getDefaultSortInfos(): SortInfos {
        return new SortInfos('customerId', 'asc');
    }

    /**
     * Retourne le droit necessaire pour pouvoir crééer un enregistrement.
     */
    public override getCreationRight(): Rights {
        return Rights.ArticleCreate;
    }

    /**
     * Retourne le droit necessaire pour pouvoir mettre à jour un enregistrement.
     */
    protected override getUpdateRight(): Rights {
        return Rights.ArticleEdit;
    }

    /**
     * Retourne le droit necessaire pour pouvoir supprimer un enregistrement.
     */
    protected override getDeletionRight(): Rights {
        return Rights.ArticleDelete;
    }
}
